/**
 * hdt-bottom-sheet.js
 * Web component: <hdt-bottom-sheet>
 * Phase 0.5 of Responsive Plan.
 *
 * Mobile-only iOS-style sheet that slides up from the bottom.
 * Pair with hdt-bottom-sheet.css.
 *
 * Attributes:
 *   data-snap="medium" | "full" | "auto"  — height preset
 *   open                                  — toggled by JS
 *   data-dragging="true"                  — set during touch drag
 *
 * Public API:
 *   sheet.open(snap?)   open the sheet (optionally override snap)
 *   sheet.close()       close the sheet
 *   sheet.toggle()      toggle
 *
 * External triggers (no JS needed in section):
 *   <button data-sheet-open="filter-sheet">Filter</button>
 *   <button data-sheet-close>Close</button>            (inside the sheet)
 *
 * Lifecycle events (bubbles, composed):
 *   "sheet:open"   before opening
 *   "sheet:opened" after opening transition
 *   "sheet:close"  before closing
 *   "sheet:closed" after closing transition
 */
(function () {
  if (typeof window === 'undefined' || customElements.get('hdt-bottom-sheet')) return;

  // Threshold (px) past which a downward drag closes the sheet on release.
  // Or — if drag velocity exceeds VELOCITY_DISMISS px/ms — close regardless.
  const DRAG_CLOSE_PX = 100;
  const VELOCITY_DISMISS = 0.6;

  // Track which sheet currently owns the scroll-lock — only one body class
  // is needed even if multiple sheets exist on the page.
  let savedScrollY = 0;
  let lockOwners = 0;

  function lockBodyScroll() {
    if (lockOwners === 0) {
      savedScrollY = window.scrollY || document.documentElement.scrollTop;
      document.body.style.top = `-${savedScrollY}px`;
      document.body.classList.add('hdt-bottom-sheet-open');
    }
    lockOwners += 1;
  }

  function unlockBodyScroll() {
    lockOwners = Math.max(0, lockOwners - 1);
    if (lockOwners === 0) {
      document.body.classList.remove('hdt-bottom-sheet-open');
      document.body.style.top = '';
      window.scrollTo(0, savedScrollY);
    }
  }

  class HdtBottomSheet extends HTMLElement {
    constructor() {
      super();
      this._onBackdropClick = this._onBackdropClick.bind(this);
      this._onKeyDown = this._onKeyDown.bind(this);
      this._onTouchStart = this._onTouchStart.bind(this);
      this._onTouchMove = this._onTouchMove.bind(this);
      this._onTouchEnd = this._onTouchEnd.bind(this);
      this._onCloseClick = this._onCloseClick.bind(this);

      this._dragStartY = 0;
      this._dragLastY = 0;
      this._dragStartTime = 0;
      this._panelStartHeight = 0;
      this._isDragging = false;
      this._previouslyFocused = null;
    }

    connectedCallback() {
      // Ensure required structural pieces exist; if user provided only the
      // panel, we lazily create a backdrop.
      if (!this.querySelector('[slot="backdrop"], .hdt-bottom-sheet__backdrop')) {
        const bd = document.createElement('div');
        bd.className = 'hdt-bottom-sheet__backdrop';
        this.insertBefore(bd, this.firstChild);
      }
      this._backdrop = this.querySelector('[slot="backdrop"], .hdt-bottom-sheet__backdrop');
      this._panel = this.querySelector('.hdt-bottom-sheet__panel');
      this._handle = this.querySelector('.hdt-bottom-sheet__handle');
      this._closeBtns = this.querySelectorAll('[data-sheet-close]');

      // ARIA defaults
      this.setAttribute('role', 'dialog');
      this.setAttribute('aria-modal', 'true');
      if (!this.hasAttribute('data-snap')) this.setAttribute('data-snap', 'auto');

      this._backdrop.addEventListener('click', this._onBackdropClick);
      this._closeBtns.forEach((b) => b.addEventListener('click', this._onCloseClick));

      if (this._handle) {
        this._handle.addEventListener('touchstart', this._onTouchStart, { passive: false });
        this._handle.addEventListener('touchmove', this._onTouchMove, { passive: false });
        this._handle.addEventListener('touchend', this._onTouchEnd);
        this._handle.addEventListener('touchcancel', this._onTouchEnd);
      }
    }

    disconnectedCallback() {
      this._backdrop && this._backdrop.removeEventListener('click', this._onBackdropClick);
      this._closeBtns.forEach((b) => b.removeEventListener('click', this._onCloseClick));
      if (this._handle) {
        this._handle.removeEventListener('touchstart', this._onTouchStart);
        this._handle.removeEventListener('touchmove', this._onTouchMove);
        this._handle.removeEventListener('touchend', this._onTouchEnd);
        this._handle.removeEventListener('touchcancel', this._onTouchEnd);
      }
      document.removeEventListener('keydown', this._onKeyDown);
      if (this.hasAttribute('open')) unlockBodyScroll();
    }

    // ------- Public API ----------------------------------------------------

    open(snap) {
      if (this.hasAttribute('open')) return;
      if (snap) this.setAttribute('data-snap', snap);

      this.dispatchEvent(new CustomEvent('sheet:open', { bubbles: true, composed: true }));

      this._previouslyFocused = document.activeElement;
      this.setAttribute('open', '');
      lockBodyScroll();
      document.addEventListener('keydown', this._onKeyDown);

      // Move focus inside the panel for a11y; defer until transition mounts.
      requestAnimationFrame(() => {
        const firstFocusable = this._panel && this._panel.querySelector(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (firstFocusable) firstFocusable.focus({ preventScroll: true });
      });

      this._panel && this._panel.addEventListener(
        'transitionend',
        () => this.dispatchEvent(new CustomEvent('sheet:opened', { bubbles: true, composed: true })),
        { once: true }
      );
    }

    close() {
      if (!this.hasAttribute('open')) return;
      this.dispatchEvent(new CustomEvent('sheet:close', { bubbles: true, composed: true }));

      this.removeAttribute('open');
      this.removeAttribute('data-dragging');
      if (this._panel) this._panel.style.transform = '';
      document.removeEventListener('keydown', this._onKeyDown);
      unlockBodyScroll();

      if (this._previouslyFocused && typeof this._previouslyFocused.focus === 'function') {
        try { this._previouslyFocused.focus({ preventScroll: true }); } catch (e) { /* noop */ }
      }

      // Defer "closed" event to the end of the CSS transition.
      setTimeout(() => {
        this.dispatchEvent(new CustomEvent('sheet:closed', { bubbles: true, composed: true }));
      }, 340);
    }

    toggle(snap) {
      this.hasAttribute('open') ? this.close() : this.open(snap);
    }

    // ------- Internal handlers --------------------------------------------

    _onBackdropClick() {
      this.close();
    }

    _onCloseClick(e) {
      e.preventDefault();
      this.close();
    }

    _onKeyDown(e) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        this.close();
      }
    }

    _onTouchStart(e) {
      if (!this._panel) return;
      const t = e.touches[0];
      this._dragStartY = t.clientY;
      this._dragLastY = t.clientY;
      this._dragStartTime = performance.now();
      this._panelStartHeight = this._panel.offsetHeight;
      this._isDragging = true;
      this.setAttribute('data-dragging', 'true');
    }

    _onTouchMove(e) {
      if (!this._isDragging || !this._panel) return;
      const t = e.touches[0];
      const dy = t.clientY - this._dragStartY;
      this._dragLastY = t.clientY;
      // Only react to downward drags — upward should be ignored (no resize-up support yet)
      if (dy < 0) {
        this._panel.style.transform = 'translateY(0)';
        return;
      }
      e.preventDefault();
      this._panel.style.transform = `translateY(${dy}px)`;
    }

    _onTouchEnd() {
      if (!this._isDragging || !this._panel) return;
      const dy = this._dragLastY - this._dragStartY;
      const dt = Math.max(1, performance.now() - this._dragStartTime);
      const velocity = dy / dt; // px / ms — positive = moving down
      this._isDragging = false;
      this.removeAttribute('data-dragging');

      if (dy > DRAG_CLOSE_PX || velocity > VELOCITY_DISMISS) {
        this.close();
      } else {
        // Snap back to open position
        this._panel.style.transform = '';
      }
    }
  }

  customElements.define('hdt-bottom-sheet', HdtBottomSheet);

  // ---- Global delegated opener: <button data-sheet-open="my-sheet-id"> ----
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-sheet-open]');
    if (!trigger) return;
    const id = trigger.getAttribute('data-sheet-open');
    if (!id) return;
    const sheet = document.getElementById(id);
    if (sheet && typeof sheet.open === 'function') {
      e.preventDefault();
      sheet.open();
    }
  });
})();
